import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';

// Global styles component to handle custom fonts and print styles
const GlobalStyles = () => (
  <style dangerouslySetInnerHTML={{__html: `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
    
    :root {
      --font-sans: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      --font-mono: 'JetBrains Mono', monospace;
    }

    body {
      font-family: var(--font-sans);
      background-color: #F8FAFC;
      color: #0F172A;
      -webkit-font-smoothing: antialiased;
      overflow-x: hidden;
    }

    .font-mono-custom {
      font-family: var(--font-mono);
    }

    .slide-container {
      width: 100%;
      height: 100%;
      min-height: 800px;
      background: #FFFFFF;
      border: 1px solid #E2E8F0;
      border-radius: 16px;
      box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
      padding: 3.5rem;
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    
    .presentation-viewport {
      position: relative;
      width: 100%;
      max-width: 1440px;
      margin: 0 auto;
      height: 850px;
      overflow: hidden;
    }
    
    .slide-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 0;
      pointer-events: none;
    }
    
    .slide-wrapper.active {
      transform: translateX(0);
      opacity: 1;
      pointer-events: auto;
      z-index: 10;
    }
    
    .slide-wrapper.prev {
      transform: translateX(-100%);
      opacity: 0;
      z-index: 1;
    }
    
    .slide-wrapper.next {
      transform: translateX(100%);
      opacity: 0;
      z-index: 1;
    }

    @media print {
      body { background: #FFFFFF; overflow: visible; }
      .presentation-viewport { 
         height: auto; 
         overflow: visible;
      }
      .slide-wrapper {
        position: relative;
        transform: none !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        page-break-after: always;
        height: auto;
        min-height: 100vh;
        margin-bottom: 2rem;
      }
      .slide-container {
        border: none;
        box-shadow: none;
        margin: 0;
        padding: 2.5rem;
        border-radius: 0;
      }
      .no-print { display: none !important; }
    }
  `}} />
);

// Favicon Image Helper Component
const Favicon = ({ domain, alt, className = "w-3.5 h-3.5 rounded shadow-sm bg-white" }) => (
  <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} className={className} alt={alt || domain} />
);

// Layer Badge Component
const LayerBadge = ({ children, className }) => (
  <span className={`text-[0.7rem] uppercase tracking-wider font-bold px-2.5 py-0.5 rounded-full ${className}`}>
    {children}
  </span>
);

// Parkin Pin Component for heatmaps
const ParkinPin = ({ label = "PARKIN" }) => (
  <span className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded bg-slate-900 text-teal-400 text-[9px] font-bold font-mono-custom tracking-wider shadow-sm border border-slate-700">
    <span>📍</span><span>{label}</span>
  </span>
);

// Reusable Slide Wrapper
const Slide = ({ children, pageNumber, totalPages, sectionTitle, slideTitle, subtitle, sourceText = "Sources: DFM Public Filings; RTA Mobility Strategy 2030; Global Equity Research" }) => (
  <section className="slide-container">
    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
      <div>
        <span className="text-xs font-bold text-teal-600 uppercase tracking-wider font-mono-custom">
          {sectionTitle}
        </span>
        <h2 className="text-2xl font-bold text-slate-900 mt-1">{slideTitle}</h2>
      </div>
      <div className="text-right text-xs text-slate-400 max-w-sm font-medium leading-relaxed">
        {subtitle}
      </div>
    </div>
    
    {children}

    <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-400 mt-auto">
      <div className="font-semibold text-slate-600">Parkin PJSC Strategic Review</div>
      <div>{sourceText}</div>
      <div className="font-mono-custom">Slide {String(pageNumber).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}</div>
    </div>
  </section>
);

const Header = ({ currentSlide, totalSlides, onPrev, onNext, onJumpTo }) => (
  <div className="max-w-[1440px] mx-auto mb-6 flex items-center justify-between no-print px-2 mt-4 relative z-50">
    <div className="flex items-center space-x-3">
      <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">P</div>
      <div>
        <h1 className="font-bold text-sm text-slate-800">Parkin PJSC (DFM: PARKIN) | Board Presentation</h1>
        <p className="text-xs text-slate-500">AED 1.5B Capital Allocation & Smart Mobility Platform Strategy</p>
      </div>
    </div>
    
    <div className="flex items-center space-x-4 bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-200">
      <button 
        onClick={onPrev} 
        disabled={currentSlide === 0}
        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition disabled:opacity-30 disabled:hover:bg-transparent"
        aria-label="Previous Slide"
      >
        <ArrowLeft size={18} />
      </button>
      
      <div className="flex items-center space-x-1">
        {Array.from({ length: totalSlides }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => onJumpTo(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              currentSlide === idx ? 'bg-teal-600 w-4' : 'bg-slate-200 hover:bg-slate-300'
            }`}
            aria-label={`Jump to slide ${idx + 1}`}
          />
        ))}
      </div>

      <button 
        onClick={onNext} 
        disabled={currentSlide === totalSlides - 1}
        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition disabled:opacity-30 disabled:hover:bg-transparent"
        aria-label="Next Slide"
      >
        <ArrowRight size={18} />
      </button>
    </div>

    <div className="flex items-center space-x-4 text-xs">
      <button 
        onClick={() => window.print()} 
        className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg shadow-sm transition"
      >
        Print / Export PDF
      </button>
    </div>
  </div>
);

const Slide1 = ({ pageNumber, totalPages }) => (
  <section className="slide-container">
    <div className="flex items-center justify-between border-b border-slate-100 pb-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="px-3 py-1 bg-teal-50 text-teal-700 text-[10px] sm:text-xs font-bold rounded-full border border-teal-200">
          CONFIDENTIAL — FOR BOARD REVIEW ONLY
        </span>
        <span className="px-3 py-1 bg-slate-100 text-slate-600 text-[10px] sm:text-xs font-bold rounded-full border border-slate-200 uppercase tracking-wider">
          INDEPENDENT OUTSIDE-IN EVALUATION
        </span>
        <span className="text-xs text-slate-400 font-mono-custom hidden sm:inline-block">DOC ID: PRKN-BOD-2026-ST</span>
      </div>
      <div className="text-right text-xs text-slate-500 flex-shrink-0">
        Status Date: <span className="font-semibold text-slate-700">September 2026</span>
      </div>
    </div>

    <div className="my-auto py-8 grid grid-cols-12 gap-12 items-center">
      {/* Left Column: Core Narrative & Disclaimer */}
      <div className="col-span-7 pr-4">
        <div className="inline-flex items-center space-x-2 text-xs font-bold tracking-widest text-teal-600 uppercase mb-4">
          <span>Capital Allocation & Strategic Transformation</span>
        </div>
        <h1 className="text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
          Transitioning Parkin from a Static Concessionaire to a <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-emerald-600">Smart Mobility Platform</span>
        </h1>
        <p className="text-lg text-slate-600 font-normal leading-relaxed mb-10">
          Deploying the <strong className="text-slate-900 font-semibold">AED 1.5 Billion Strategic War Chest</strong> across an integrated 4-Layer Architecture to capture higher software multiples, consolidate domestic curbs, and export asset-light platform services across the GCC.
        </p>

        {/* Basis of Preparation Notice Box */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start space-x-3 shadow-sm">
          <div className="mt-0.5 text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
          </div>
          <div>
            <h4 className="text-xs font-bold text-slate-700 tracking-wider uppercase mb-1 font-mono-custom">Basis of Preparation & Advisory Notice</h4>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              This document represents an unconstrained, <strong>outside-in strategic assessment</strong> developed exclusively from public filings, regulatory data, and global mobility comps. Because no non-public management or board context was provided, analytical horizons, strategic vectors, and capital allocations are indicative external hypotheses. Insights and targets are subject to calibration once proprietary internal constraints and operational realities are factored in.
            </p>
          </div>
        </div>
      </div>

      {/* Right Column: Financial Baseline Dashboard */}
      <div className="col-span-5 relative">
        <div className="bg-[#0F172A] rounded-2xl p-8 text-white shadow-2xl relative overflow-hidden border border-slate-800">
          {/* Decorative background glow */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-slate-800/80">
              <span className="text-xs font-bold tracking-widest text-slate-400 uppercase font-mono-custom">Q2 2026 Baseline</span>
              <span className="px-2 py-1 bg-teal-500/20 text-teal-300 text-[10px] font-bold rounded uppercase tracking-wider border border-teal-500/30">Operating Context</span>
            </div>
            
            <div className="grid grid-cols-2 gap-y-10 gap-x-6">
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1.5 tracking-wider">Managed Portfolio</div>
                <div className="text-3xl font-bold text-white font-mono-custom mb-1.5">268.3k</div>
                <div className="flex flex-col space-y-0.5 text-[11px]">
                  <span className="text-emerald-400 font-medium">+27% YoY Growth</span>
                  <span className="text-slate-400">203k Public | 61.5k Private</span>
                </div>
              </div>
              
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1.5 tracking-wider">Run-Rate EBITDA</div>
                <div className="text-3xl font-bold text-white font-mono-custom mb-1.5">60%</div>
                <div className="flex flex-col space-y-0.5 text-[11px]">
                  <span className="text-slate-300 font-medium">Record Margin Setup</span>
                  <span className="text-slate-400">AED 217.2M (Q2 2026)</span>
                </div>
              </div>
              
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1.5 tracking-wider">Cash Conversion</div>
                <div className="text-3xl font-bold text-teal-400 font-mono-custom mb-1.5">96–99%</div>
                <div className="flex flex-col space-y-0.5 text-[11px]">
                  <span className="text-slate-300 font-medium">High Liquidity</span>
                  <span className="text-slate-400">Statutory capex shield</span>
                </div>
              </div>
              
              <div>
                <div className="text-[10px] uppercase text-slate-400 font-semibold mb-1.5 tracking-wider">War Chest Capacity</div>
                <div className="text-3xl font-bold text-white font-mono-custom mb-1.5">AED 1.5B</div>
                <div className="flex flex-col space-y-0.5 text-[11px]">
                  <span className="text-slate-300 font-medium">Ready to Deploy</span>
                  <span className="text-slate-400">Cash & leverage reserves</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="flex items-center justify-between pt-6 border-t border-slate-100 text-xs text-slate-400 mt-auto">
      <div>Prepared by: <span className="font-semibold text-slate-700">Frontier | Strategy & Agents</span> &bull; Balbinder Singh Bhatia</div>
      <div>Sources: Parkin PJSC Q2 2026 Financial Reports; DFM IPO Prospectus (Feb 2024); RTA 49-Yr Concession Agreement; Board Treasury Review</div>
      <div>Slide {String(pageNumber).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}</div>
    </div>
  </section>
);

const Slide2 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="MACRO DISRUPTIONS & THE MEGATREND"
    slideTitle="The Great Curb Re-Platforming"
    subtitle="Transitioning from passive asset storage to active urban orchestration and decentralized energy"
    sourceText="Sources: Dubai Autonomous Transportation Strategy 2030; UAE National EV Policy (Res. 81/2023); WEF &quot;Future of the Urban Curb&quot;"
  >
    <div className="flex flex-col h-full py-2 space-y-4">
      
      {/* Dark Banner */}
      <div className="bg-[#0F172A] rounded-xl p-5 text-white flex-shrink-0 shadow-md">
        <h3 className="text-teal-400 font-bold text-lg mb-1.5">The Overarching Megaparadigm</h3>
        <p className="text-sm text-slate-300 leading-relaxed">
          For 70 years, the economic model of parking was static real estate: renting a 15-sqm asphalt box to a stationary metal box for AED 4/hour. Today, the curb is transforming into a <strong className="text-white">high-frequency, programmable digital marketplace</strong> where space is dynamically yielded per-minute for freight, micro-mobility, EV power delivery, and autonomous staging.
        </p>
      </div>

      {/* 4 Cards Grid */}
      <div className="grid grid-cols-4 gap-4 flex-1 min-h-0">
        {/* Card 1 */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col justify-between overflow-hidden">
          <div className="p-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 mb-3 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>
            </div>
            <div className="text-center mb-4">
              <span className="text-[10px] font-bold text-teal-600 uppercase tracking-widest font-mono-custom block mb-1">01. CURB MONETIZATION</span>
              <h4 className="font-bold text-slate-900 text-base leading-tight">Dynamic Quick-Commerce</h4>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">GLOBAL REALITY</span>
                <p className="text-xs text-slate-600 leading-relaxed">Global urban delivery stops up 2.5x since 2020. Cities are transitioning to variable, per-minute commercial loading zones.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">REGIONAL CATALYST</span>
                <p className="text-xs text-slate-600 leading-relaxed">High Gulf delivery density; 80%+ of short courier micro-dwells currently escape static parking billing entirely.</p>
              </div>
            </div>
          </div>
          <div className="px-4 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between mt-auto">
            <span className="text-[10px] font-bold text-slate-500">Strategic Shift:</span>
            <span className="text-xs font-bold text-teal-700">Static ➔ Dynamic</span>
          </div>
        </div>

        {/* Card 2 */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col justify-between overflow-hidden">
          <div className="p-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 mb-3 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M3 12a9 9 0 1 0 18 0 9 9 0 1 0-18 0"></path></svg>
            </div>
            <div className="text-center mb-4">
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest font-mono-custom block mb-1">02. AMBIENT SENSING</span>
              <h4 className="font-bold text-slate-900 text-base leading-tight">Frictionless Computer Vision</h4>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">GLOBAL REALITY</span>
                <p className="text-xs text-slate-600 leading-relaxed">Smart cities are abandoning sporadic drive-by enforcement for fixed edge-AI optical sensors capturing 100% of dwells.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">REGIONAL CATALYST</span>
                <p className="text-xs text-slate-600 leading-relaxed">Smart-city zero-friction mandates require seamless vehicle integration with sovereign digital IDs and national wallets.</p>
              </div>
            </div>
          </div>
          <div className="px-4 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between mt-auto">
            <span className="text-[10px] font-bold text-slate-500">Strategic Shift:</span>
            <span className="text-xs font-bold text-indigo-700">Patrols ➔ 24/7 Optical</span>
          </div>
        </div>

        {/* Card 3 */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col justify-between overflow-hidden">
          <div className="p-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mb-3 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
            </div>
            <div className="text-center mb-4">
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest font-mono-custom block mb-1">03. ENERGY CONVERGENCE</span>
              <h4 className="font-bold text-slate-900 text-base leading-tight">Decentralized Micro-Utilities</h4>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">GLOBAL REALITY</span>
                <p className="text-xs text-slate-600 leading-relaxed">Charging happens at dwell destinations. MSCPs are integrating battery storage and load-balancing software (CPMS).</p>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">REGIONAL CATALYST</span>
                <p className="text-xs text-slate-600 leading-relaxed">UAE 50% EV target by 2050. Rapid EV adoption threatens grid substations without smart software load orchestration.</p>
              </div>
            </div>
          </div>
          <div className="px-4 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between mt-auto">
            <span className="text-[10px] font-bold text-slate-500">Strategic Shift:</span>
            <span className="text-xs font-bold text-emerald-700">Garages ➔ Smart EV Hubs</span>
          </div>
        </div>

        {/* Card 4 */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col justify-between overflow-hidden">
          <div className="p-4 flex-1">
            <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 mb-3 mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a2 2 0 0 0-1.6-.8H9.3a2 2 0 0 0-1.6.8L5 11l-5.16.86a1 1 0 0 0-.84.99V16h3m10 0a2 2 0 1 1-4 0m4 0a2 2 0 1 0-4 0m-6 0a2 2 0 1 1-4 0m4 0a2 2 0 1 0-4 0"></path></svg>
            </div>
            <div className="text-center mb-4">
              <span className="text-[10px] font-bold text-purple-600 uppercase tracking-widest font-mono-custom block mb-1">04. AV FLEET STAGING</span>
              <h4 className="font-bold text-slate-900 text-base leading-tight">Autonomous Turnaround</h4>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">GLOBAL REALITY</span>
                <p className="text-xs text-slate-600 leading-relaxed">Commercial robotaxis require dedicated mid-day turnaround hubs for automated inductive charging, cleaning, and staging.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">REGIONAL CATALYST</span>
                <p className="text-xs text-slate-600 leading-relaxed">Dubai RTA 25% autonomous trip mandate by 2030 accelerates commercial fleet deployment faster than Western markets.</p>
              </div>
            </div>
          </div>
          <div className="px-4 py-3.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between mt-auto">
            <span className="text-[10px] font-bold text-slate-500">Strategic Shift:</span>
            <span className="text-xs font-bold text-purple-700">Storage ➔ B2B Depots</span>
          </div>
        </div>

      </div>
    </div>
  </Slide>
);

const Slide3 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="MODERN MOBILITY ECONOMICS"
    slideTitle="The Mobility Value Chain Economics"
    subtitle="How curb re-platforming unbundles industry economics: A capital allocation filter separating high-multiple software from margin-dilutive hardware"
    sourceText="Sources: Goldman Sachs & Morgan Stanley Mobility Research; PitchBook Mobility Tech Indices; KeyBanc / Bessemer SaaS Comps"
  >
    <div className="flex flex-col h-full py-2 space-y-4">
      
      {/* Strategic Premise Banner */}
      <div className="flex items-center p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <span className="px-3 py-1 bg-slate-900 text-white text-[10px] font-bold rounded-md tracking-wider mr-4 flex-shrink-0">STRATEGIC PREMISE</span>
        <p className="text-sm text-slate-700 leading-relaxed">
          As static curbs transform into dynamic mobility nodes, the industry's economic value has <span className="text-indigo-600 font-semibold">unbundled</span> from physical concrete into <span className="text-indigo-600 font-semibold">distinct digital, software, and hardware tiers</span>. <strong className="text-slate-900">This architecture dictates where we allocate our capital.</strong>
        </p>
      </div>

      <div className="grid grid-cols-12 gap-5 flex-1 min-h-0">
        {/* Left Column: 4 Layers */}
        <div className="col-span-8 flex flex-col justify-between space-y-3">
          
          {/* Layer 4 */}
          <div className="flex-1 p-4 rounded-xl border border-teal-200 bg-teal-50 shadow-sm flex flex-col justify-center relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-teal-500"></div>
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">DRIVEN BY: DIGITAL WALLET & IN-DASH CONVERGENCE</span>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <span className="px-2.5 py-0.5 bg-teal-500 text-white text-[10px] font-bold rounded-full tracking-wider">CONSUMER INTERFACE</span>
                <h3 className="font-bold text-slate-900 text-sm">MaaS Wallets & Digital Clearing</h3>
              </div>
              <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-bold rounded font-mono-custom border border-teal-100">45%-60% Closed-Loop EBITDA | 16x-22x Multiple</span>
            </div>
            <p className="text-[11px] text-slate-600">Consumer super-apps, connected vehicle in-dash OEM wallets, automated parking & toll clearing, captive dwell monetization.</p>
          </div>

          {/* Layer 3 */}
          <div className="flex-1 p-4 rounded-xl border border-indigo-200 bg-indigo-50 shadow-sm flex flex-col justify-center relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500"></div>
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">DRIVEN BY: DYNAMIC QUICK-COMMERCE YIELD & CPMS</span>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <span className="px-2.5 py-0.5 bg-indigo-500 text-white text-[10px] font-bold rounded-full tracking-wider">PLATFORM SOFTWARE</span>
                <h3 className="font-bold text-slate-900 text-sm">Fleet Orchestration & Yield Management</h3>
              </div>
              <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[10px] font-bold rounded font-mono-custom border border-indigo-100">70%-85% SaaS Gross Margin | 18x-25x Multiple</span>
            </div>
            <p className="text-[11px] text-slate-600">Curbside dynamic yield pricing engines, Cloud Charge Point Management Systems (CPMS), and BPO violation dispute platforms.</p>
          </div>

          {/* Layer 2 */}
          <div className="flex-1 p-4 rounded-xl border border-amber-200 bg-amber-50 shadow-sm flex flex-col justify-center relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500"></div>
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">DRIVEN BY: CONTINUOUS 24/7 OPTICAL SENSING</span>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <span className="px-2.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full tracking-wider">EDGE HARDWARE</span>
                <h3 className="font-bold text-slate-900 text-sm">Sensors, Vision & Telemetry</h3>
              </div>
              <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded font-mono-custom border border-amber-100">20%-35% Hardware Margin | 6x-9x Multiple</span>
            </div>
            <p className="text-[11px] text-slate-600">Continuous optical edge-AI cameras, mobile smart inspection vehicles, smart meters, and EV charging dispenser manufacturing.</p>
          </div>

          {/* Layer 1 */}
          <div className="flex-1 p-4 rounded-xl border border-slate-200 bg-slate-100 shadow-sm flex flex-col justify-center relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-slate-700"></div>
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">DRIVEN BY: CONCESSIONS, MSCPS & ROBOTAXI STAGING</span>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <span className="px-2.5 py-0.5 bg-slate-700 text-white text-[10px] font-bold rounded-full tracking-wider">PHYSICAL ASSETS</span>
                <h3 className="font-bold text-slate-900 text-sm">Curbside & Real Estate Foundation</h3>
              </div>
              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-bold rounded font-mono-custom border border-slate-200">55%-65% (Parkin Peak) Margin | 8x-11x Multiple</span>
            </div>
            <p className="text-[11px] text-slate-600">On-street rights-of-way, multi-storey car parks (MSCPs), off-street private lots, robotaxi staging yards, and vertiport nodes.</p>
          </div>

        </div>

        {/* Right Column: Board Mandate Card */}
        <div className="col-span-4 bg-[#0F172A] rounded-xl p-5 text-white flex flex-col shadow-lg border border-slate-800">
          <div className="flex items-center justify-between mb-5">
            <span className="text-[9px] font-bold text-slate-400 tracking-widest uppercase">BOARD MANDATE</span>
            <span className="text-[9px] font-bold font-mono-custom text-slate-300 border border-slate-700 px-2 py-0.5 rounded bg-slate-800/50">AED 1.5B ALLOCATION</span>
          </div>
          
          <h3 className="text-lg font-bold text-white mb-6">The Capital Allocation Dilemma</h3>
          
          <div className="space-y-6 flex-1">
            {/* Trap 1 */}
            <div className="flex items-start space-x-3">
              <div className="mt-0.5 text-rose-500 bg-rose-500/10 p-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-rose-400 tracking-wider uppercase mb-1">THE TRAP: PHYSICAL DILUTION</h4>
                <p className="text-[11px] text-slate-300 leading-snug">Dubai physical curbs are bounded. Pouring concrete or buying foreign garages yields only 8x-11x multiples with high concession renewal risk.</p>
              </div>
            </div>
            
            {/* Trap 2 */}
            <div className="flex items-start space-x-3">
              <div className="mt-0.5 text-amber-500 bg-amber-500/10 p-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-amber-400 tracking-wider uppercase mb-1">THE PITFALL: HARDWARE EROSION</h4>
                <p className="text-[11px] text-slate-300 leading-snug">Deploying pure sensor hardware delivers only 20%-35% gross margins, which actively dilutes Parkin's record ~60% EBITDA margin.</p>
              </div>
            </div>

            {/* Target */}
            <div className="flex items-start space-x-3">
              <div className="mt-0.5 text-emerald-400 bg-emerald-400/10 p-1 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-emerald-400 tracking-wider uppercase mb-1">THE TARGET: SOFTWARE RE-RATING</h4>
                <p className="text-[11px] text-slate-300 leading-snug">Software delivers 70%+ SaaS margins & 18x-25x multiples with zero civil capex. Consumer interfaces capture gross transaction value.</p>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-700/50">
            <span className="text-[11px] text-slate-400 font-medium mr-1 block mb-1">Strategic Verdict:</span>
            <span className="text-[11px] text-slate-200 leading-snug block">Parkin's AED 1.5B must be deployed <strong className="text-teal-400 font-bold">strictly upward</strong>—funding Software IP and Interface clearing, while outsourcing commoditized hardware.</span>
          </div>
        </div>

      </div>
    </div>
  </Slide>
);

const Slide4 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="GRANULAR MARKET SEGMENTATION"
    slideTitle="Market Opportunity Heatmap: Sizing the Alpha"
    subtitle="Objective market assessment separating high-ROIC growth targets from commoditized traps"
    sourceText="Sources: Parkin Operational Assets Registry; McKinsey &quot;Curbside Economy&quot;; Frost & Sullivan Smart Mobility; RTA Strategic Plan 2030"
  >
    <div className="flex flex-col h-full py-2">
      
      {/* Legend */}
      <div className="flex items-center justify-between mb-3 px-2">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2 text-xs">
            <span className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300"></span>
            <span className="font-semibold text-slate-700">Alpha Growth</span>
            <span className="text-slate-400">(High ROIC, Asset-Light)</span>
          </div>
          <div className="flex items-center space-x-2 text-xs">
            <span className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-300"></span>
            <span className="font-semibold text-slate-700">Defensive Core</span>
            <span className="text-slate-400">(Stable Monopolies, Cash Cows)</span>
          </div>
          <div className="flex items-center space-x-2 text-xs">
            <span className="w-3 h-3 rounded-sm bg-slate-100 border border-slate-300"></span>
            <span className="font-semibold text-slate-700">Commoditized Trap</span>
            <span className="text-slate-400">(High Capex, Low Moat)</span>
          </div>
        </div>
        <div className="flex items-center space-x-2 text-xs bg-slate-50 px-3 py-1 rounded-lg border border-slate-200">
          <ParkinPin label="PARKIN STATUS" />
          <span className="text-slate-500 font-medium ml-2">Estimated position (External market context basis)</span>
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-between space-y-2.5">
        
        {/* Interface Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden">
          <div className="w-12 flex-shrink-0 bg-teal-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">INTERFACE</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-2.5 p-2.5 bg-white">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col justify-between relative shadow-[0_0_0_2px_rgba(20,184,166,0.3)]">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Sovereign & Regional Wallets</span>
                <p className="text-[10px] text-slate-500 leading-snug">Captive national payment rails (like Salik) and municipal tolling accounts offering seamless auto-debit.</p>
              </div>
              <div className="mt-2"><ParkinPin label="PARKIN APP" /></div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Consumer Super-App Integrations</span>
                <p className="text-[10px] text-slate-500 leading-snug">High-frequency mobility, ride-hailing, and delivery app ecosystems leveraging existing captive daily active users.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-emerald-700 font-mono-custom font-bold bg-emerald-100 px-1.5 py-0.5 rounded">WHITESPACE</span></div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">In-Dash Connected Car (OEMs)</span>
                <p className="text-[10px] text-slate-500 leading-snug">Direct, integrated payments natively embedded in OEM vehicle dashboard screens, bypassing mobile apps entirely.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-emerald-700 font-mono-custom font-bold bg-emerald-100 px-1.5 py-0.5 rounded">WHITESPACE</span></div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-500 leading-tight block mb-1">Aggregator White-Label Apps</span>
                <p className="text-[10px] text-slate-400 leading-snug">Fragmented third-party parking payment interfaces competing on thin interchange margins and heavy marketing.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-slate-500 font-mono-custom font-bold bg-slate-200 px-1.5 py-0.5 rounded">OUT OF SCOPE</span></div>
            </div>
          </div>
        </div>

        {/* Software Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden">
          <div className="w-12 flex-shrink-0 bg-indigo-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">SOFTWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-2.5 p-2.5 bg-white">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Dynamic Curbside Yield</span>
                <p className="text-[10px] text-slate-500 leading-snug">Algorithmic, per-minute pricing engines designed to monetize high-turnover delivery and commercial fleet dwells.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-emerald-700 font-mono-custom font-bold bg-emerald-100 px-1.5 py-0.5 rounded">WHITESPACE</span></div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Cloud CPMS & EV Grid</span>
                <p className="text-[10px] text-slate-500 leading-snug">Remote cloud management, smart grid load balancing, and dynamic tariff setting for destination EV networks.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-emerald-700 font-mono-custom font-bold bg-emerald-100 px-1.5 py-0.5 rounded">WHITESPACE</span></div>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col justify-between relative shadow-[0_0_0_2px_rgba(20,184,166,0.3)]">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Municipal Dispute Engine</span>
                <p className="text-[10px] text-slate-500 leading-snug">Core operational backend for digital ticket issuance, automated fine processing, and citizen dispute resolution.</p>
              </div>
              <div className="mt-2"><ParkinPin label="PARKIN PMS" /></div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-500 leading-tight block mb-1">Legacy On-Premise PMS</span>
                <p className="text-[10px] text-slate-400 leading-snug">Fragmented, server-based local garage software burdened by heavy integration debt and localized maintenance.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-slate-500 font-mono-custom font-bold bg-slate-200 px-1.5 py-0.5 rounded">AVOID</span></div>
            </div>
          </div>
        </div>

        {/* Hardware Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden">
          <div className="w-12 flex-shrink-0 bg-amber-500 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">HARDWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-2.5 p-2.5 bg-white">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Fixed Edge-AI Sensors</span>
                <p className="text-[10px] text-slate-500 leading-snug">Overhead, pole-mounted cameras capturing 100% of curbside dwells with frictionless, zero-labor AI vision.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-emerald-700 font-mono-custom font-bold bg-emerald-100 px-1.5 py-0.5 rounded">WHITESPACE</span></div>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col justify-between relative shadow-[0_0_0_2px_rgba(20,184,166,0.3)]">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Smart Scan Inspection Fleets</span>
                <p className="text-[10px] text-slate-500 leading-snug">Mobile patrol vehicles equipped with ANPR cameras for active municipal enforcement across sprawling zones.</p>
              </div>
              <div className="mt-2"><ParkinPin label="37 CAR FLEET" /></div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-500 leading-tight block mb-1">EV Fast Dispensers & HW</span>
                <p className="text-[10px] text-slate-400 leading-snug">Physical AC/DC electric vehicle charging units facing heavy upfront capital costs and rapid hardware obsolescence.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-slate-500 font-mono-custom font-bold bg-slate-200 px-1.5 py-0.5 rounded">OUTSOURCE</span></div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-between relative shadow-[0_0_0_2px_rgba(20,184,166,0.3)]">
              <div>
                <span className="font-bold text-xs text-slate-500 leading-tight block mb-1">Legacy Gates & Pay Stations</span>
                <p className="text-[10px] text-slate-400 leading-snug">Legacy mechanical boom barriers, paper ticket dispensers, and payment kiosks requiring intensive physical upkeep.</p>
              </div>
              <div className="mt-2"><ParkinPin label="LEGACY METERS" /></div>
            </div>
          </div>
        </div>

        {/* Physical Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden">
          <div className="w-12 flex-shrink-0 bg-slate-700 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">PHYSICAL</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-2.5 p-2.5 bg-white">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col justify-between relative shadow-[0_0_0_2px_rgba(20,184,166,0.3)]">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Public Rights-of-Way</span>
                <p className="text-[10px] text-slate-500 leading-snug">Highly defensive municipal on-street and off-street geographic monopolies secured via long-term concessions.</p>
              </div>
              <div className="mt-2"><ParkinPin label="203K PUBLIC" /></div>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col justify-between relative shadow-[0_0_0_2px_rgba(20,184,166,0.3)]">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Private Master Communities</span>
                <p className="text-[10px] text-slate-500 leading-snug">Captive parking assets embedded within high-density retail malls, business parks, and master residential zones.</p>
              </div>
              <div className="mt-2"><ParkinPin label="61K PRIVATE" /></div>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-900 leading-tight block mb-1">Autonomous Staging & Hubs</span>
                <p className="text-[10px] text-slate-500 leading-snug">B2B infrastructure leases repurposing garage space for robotaxi staging, sensor cleaning, and vertiport nodes.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-emerald-700 font-mono-custom font-bold bg-emerald-100 px-1.5 py-0.5 rounded">WHITESPACE</span></div>
            </div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex flex-col justify-between">
              <div>
                <span className="font-bold text-xs text-slate-500 leading-tight block mb-1">Green-field Civil Works</span>
                <p className="text-[10px] text-slate-400 leading-snug">Capital-intensive construction of new concrete parking structures saddled with massive debt and long payback cycles.</p>
              </div>
              <div className="mt-2"><span className="text-[9px] text-slate-500 font-mono-custom font-bold bg-slate-200 px-1.5 py-0.5 rounded">AVOID</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Slide>
);

const Slide5 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="COMPETITIVE LANDSCAPE"
    slideTitle="Ecosystem Map: Global Incumbents & Challengers"
    subtitle="Mapping global incumbents, regional insurgents, and emerging disruptors across the Value Chain"
    sourceText="Sources: Company Regulatory Filings; CB Insights & PitchBook Mobility Maps; DFM/ADX Filings; Frontier Ecosystem Intelligence"
  >
    <div className="flex flex-col h-full py-1">
      
      {/* Disclaimer Badge */}
      <div className="flex items-center justify-end mb-1.5 px-2 space-x-2">
        <span className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-[9px] font-bold text-slate-500 font-mono-custom tracking-wider shadow-sm">
          REPRESENTATIVE SAMPLE: ~65 PLAYERS
        </span>
        <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[9px] font-bold text-amber-600 font-mono-custom tracking-wider shadow-sm">
          NON-EXHAUSTIVE
        </span>
      </div>

      <div className="flex-1 flex flex-col justify-between space-y-2">
        
        {/* Interface Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-teal-300 transition-colors">
          <div className="w-10 flex-shrink-0 bg-teal-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">INTERFACE</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-3 p-2.5 bg-white items-start">
            <div>
              <span className="text-teal-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-teal-100 pb-0.5">Sovereign & Regional</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkin.ae" /> <span className="text-teal-700 font-medium leading-tight">Parkin App (Dubai)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="salik.ae" /> <span className="text-slate-700 leading-tight">Salik (UAE)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="darb.ae" /> <span className="text-slate-700 leading-tight">Darb (Abu Dhabi)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="bahraincarparks.com" /> <span className="text-slate-700 leading-tight">Amakin (Bahrain)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="mawqif.com" /> <span className="text-slate-700 leading-tight">Mawqif (KSA)</span></span>
              </div>
            </div>
            <div>
              <span className="text-teal-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-teal-100 pb-0.5">Consumer Super-Apps</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="careem.com" /> <span className="text-slate-700 leading-tight">Careem (MENA)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="uber.com" /> <span className="text-slate-700 leading-tight">Uber (Global)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="bolt.eu" /> <span className="text-slate-700 leading-tight">Bolt (EU/EMEA)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="tier.app" /> <span className="text-slate-700 leading-tight">Tier / Dott</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="li.me" /> <span className="text-slate-700 leading-tight">Lime Mobility</span></span>
              </div>
            </div>
            <div>
              <span className="text-teal-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-teal-100 pb-0.5">In-Dash OEM & Connected</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="apple.com" /> <span className="text-slate-700 leading-tight">Apple CarPlay</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="android.com" /> <span className="text-slate-700 leading-tight">Android Auto</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="parkopedia.com" /> <span className="text-slate-700 leading-tight">Parkopedia</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="mercedes-benz.com" /> <span className="text-slate-700 leading-tight">Mercedes-Benz MBUX</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="cerence.com" /> <span className="text-slate-700 leading-tight">Cerence Pay</span></span>
              </div>
            </div>
            <div>
              <span className="text-teal-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-teal-100 pb-0.5">Aggregators / White-Label</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="easypark.com" /> <span className="text-slate-700 leading-tight">EasyPark / RingGo</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="justpark.com" /> <span className="text-slate-700 leading-tight">JustPark</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="parkmobile.io" /> <span className="text-slate-700 leading-tight">ParkMobile</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="paybyphone.com" /> <span className="text-slate-700 leading-tight">PayByPhone</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="way.com" /> <span className="text-slate-700 leading-tight">Way.com</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Software Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-indigo-300 transition-colors">
          <div className="w-10 flex-shrink-0 bg-indigo-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">SOFTWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-3 p-2.5 bg-white items-start">
            <div>
              <span className="text-indigo-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-indigo-100 pb-0.5">Dynamic Curbside Yield</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="metropolis.io" /> <span className="text-slate-700 leading-tight">Metropolis OS</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="cleverciti.com" /> <span className="text-slate-700 leading-tight">Cleverciti</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="populus.ai" /> <span className="text-slate-700 leading-tight">Populus</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="arcadis.com" /> <span className="text-slate-700 leading-tight">CurbIQ (Arcadis)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="iomob.net" /> <span className="text-slate-700 leading-tight">Iomob</span></span>
              </div>
            </div>
            <div>
              <span className="text-indigo-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-indigo-100 pb-0.5">Cloud CPMS & EV Grid</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="ampeco.com" /> <span className="text-slate-700 leading-tight">AMPECO</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="driivz.com" /> <span className="text-slate-700 leading-tight">Driivz</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="virta.global" /> <span className="text-slate-700 leading-tight">Virta</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="chargepoint.com" /> <span className="text-slate-700 leading-tight">ChargePoint Cloud</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="greenflux.com" /> <span className="text-slate-700 leading-tight">GreenFlux</span></span>
              </div>
            </div>
            <div>
              <span className="text-indigo-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-indigo-100 pb-0.5">Municipal Dispute Engine</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkin.ae" /> <span className="text-teal-700 font-medium leading-tight">Parkin PMS (Dubai)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="conduent.com" /> <span className="text-slate-700 leading-tight">Conduent</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="passportinc.com" /> <span className="text-slate-700 leading-tight">Passport Labs</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="verramobility.com" /> <span className="text-slate-700 leading-tight">Verra Mobility</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="duncansolutions.com" /> <span className="text-slate-700 leading-tight">Duncan Solutions</span></span>
              </div>
            </div>
            <div>
              <span className="text-indigo-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-indigo-100 pb-0.5">Legacy On-Premise PMS</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkonic.com" /> <span className="text-amber-700 font-semibold leading-tight">Parkonic OS</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="stc.com.sa" /> <span className="text-slate-700 leading-tight">solutions by stc</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="skidata.com" /> <span className="text-slate-700 leading-tight">SKIDATA Connect</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="scheidt-bachmann.com" /> <span className="text-slate-700 leading-tight">Scheidt & Bachmann</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="hubparking.com" /> <span className="text-slate-700 leading-tight">Hub Parking</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Hardware Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-amber-300 transition-colors">
          <div className="w-10 flex-shrink-0 bg-amber-500 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">HARDWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-3 p-2.5 bg-white items-start">
            <div>
              <span className="text-amber-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-amber-100 pb-0.5">Fixed Edge-AI Sensors</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="automotus.co" /> <span className="text-slate-700 leading-tight">Automotus</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="tagmaster.com" /> <span className="text-slate-700 leading-tight">TagMaster</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="parquery.com" /> <span className="text-slate-700 leading-tight">Parquery</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="smartmicro.com" /> <span className="text-slate-700 leading-tight">Smart Micro</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="vivacitylabs.com" /> <span className="text-slate-700 leading-tight">Vivacity Labs</span></span>
              </div>
            </div>
            <div>
              <span className="text-amber-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-amber-100 pb-0.5">Smart Scan Fleets</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkin.ae" /> <span className="text-teal-700 font-medium leading-tight">Parkin Scan Cars</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="tattile.com" /> <span className="text-slate-700 leading-tight">Tattile</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="adaptiverecognition.com" /> <span className="text-slate-700 leading-tight">Adaptive Recognition</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="jenoptik.com" /> <span className="text-slate-700 leading-tight">Jenoptik</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="genetec.com" /> <span className="text-slate-700 leading-tight">Genetec (AutoVu)</span></span>
              </div>
            </div>
            <div>
              <span className="text-amber-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-amber-100 pb-0.5">EV Fast Dispensers</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="abb.com" /> <span className="text-slate-700 leading-tight">ABB E-mobility</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="wallbox.com" /> <span className="text-slate-700 leading-tight">Wallbox</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="evbox.com" /> <span className="text-slate-700 leading-tight">EVBox</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="alfen.com" /> <span className="text-slate-700 leading-tight">Alfen</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="kempower.com" /> <span className="text-slate-700 leading-tight">Kempower / Tritium</span></span>
              </div>
            </div>
            <div>
              <span className="text-amber-800 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-amber-100 pb-0.5">Legacy Gates & Pay Stations</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkin.ae" /> <span className="text-teal-700 font-medium leading-tight">Parkin Legacy Meters</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="parkonic.com" /> <span className="text-amber-700 font-semibold leading-tight">Parkonic Gate HW</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="hikvision.com" /> <span className="text-slate-700 leading-tight">Hikvision</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="nedap.com" /> <span className="text-slate-700 leading-tight">Nedap</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="flowbird.group" /> <span className="text-slate-700 leading-tight">Flowbird</span></span>
              </div>
            </div>
          </div>
        </div>

        {/* Physical Tier */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-slate-400 transition-colors">
          <div className="w-10 flex-shrink-0 bg-slate-700 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[10px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">PHYSICAL</span>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-3 p-2.5 bg-white items-start">
            <div>
              <span className="text-slate-600 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-slate-100 pb-0.5">Public Rights-of-Way</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkin.ae" /> <span className="text-teal-700 font-medium leading-tight">Parkin (203k Public)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="apcoa.com" /> <span className="text-slate-700 leading-tight">APCOA Parking</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="group-indigo.com" /> <span className="text-slate-700 leading-tight">Indigo Group</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="mawqif.com" /> <span className="text-slate-700 leading-tight">Mawqif (KSA)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="q-park.com" /> <span className="text-slate-700 leading-tight">Q-Park</span></span>
              </div>
            </div>
            <div>
              <span className="text-slate-600 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-slate-100 pb-0.5">Private Master Communities</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="parkin.ae" /> <span className="text-teal-700 font-medium leading-tight">Parkin (61k Private)</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="emaar.com" /> <span className="text-slate-700 leading-tight">Emaar</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="aldar.com" /> <span className="text-slate-700 leading-tight">Aldar Properties</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="majidalfuttaim.com" /> <span className="text-slate-700 leading-tight">Majid Al Futtaim</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="nakheel.com" /> <span className="text-slate-700 leading-tight">Nakheel</span></span>
              </div>
            </div>
            <div>
              <span className="text-slate-600 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-slate-100 pb-0.5">AV Staging & Hubs</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="getcruise.com" /> <span className="text-slate-700 leading-tight">GM Cruise</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="skyports.net" /> <span className="text-slate-700 leading-tight">Skyports Infra</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="jobyaviation.com" /> <span className="text-slate-700 leading-tight">Joby Aviation</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="archer.com" /> <span className="text-slate-700 leading-tight">Archer Aviation</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="volocopter.com" /> <span className="text-slate-700 leading-tight">Volocopter</span></span>
              </div>
            </div>
            <div>
              <span className="text-slate-600 font-bold mb-1 block text-[9.5px] uppercase tracking-wider font-mono-custom border-b border-slate-100 pb-0.5">Logistics & Greenfield</span>
              <div className="space-y-1 text-[11px] pt-0.5">
                <span className="flex items-center space-x-1.5"><Favicon domain="amazon.ae" /> <span className="text-slate-700 leading-tight">Amazon Hubs</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="noon.com" /> <span className="text-slate-700 leading-tight">Noon Express</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="talabat.com" /> <span className="text-slate-700 leading-tight">Talabat Mart</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="aramex.com" /> <span className="text-slate-700 leading-tight">Aramex</span></span>
                <span className="flex items-center space-x-1.5"><Favicon domain="besix.com" /> <span className="text-slate-700 leading-tight">BESIX (Greenfield)</span></span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </Slide>
);

const Slide6 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="COMPETITIVE DYNAMICS & PRECEDENTS"
    slideTitle="Global Playbooks: M&A, Take-Privates & Consolidations"
    subtitle="Empirical proof of how global market leaders deploy capital to capture Alpha segments"
    sourceText="Sources: SEC Filings (Metropolis/SP+); Vitruvian & Verdane Disclosures (EasyPark); PAI Partners / Stonepeak; DFM Disclosures (Salik)"
  >
    <div className="flex flex-col h-full py-1">
      
      {/* Top Banner */}
      <div className="bg-slate-900 rounded-xl p-3 text-white mb-2 flex items-center justify-between shadow-lg relative overflow-hidden flex-shrink-0">
        <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="relative z-10">
          <span className="text-[10px] font-bold font-mono-custom tracking-widest text-teal-400 uppercase block mb-0.5">Strategic Premise: Capital Allocation Lessons</span>
          <h3 className="text-xs font-medium text-slate-200 max-w-4xl leading-relaxed">Global champions are not building from scratch. They are deploying massive capital pools through M&A roll-ups, take-privates, and utility JVs to decouple their valuation multiples from legacy concrete.</h3>
        </div>
      </div>

      {/* Main Body: 5 Precedents */}
      <div className="flex-1 flex flex-col space-y-1.5 min-h-0">
        
        {/* Row 1: Metropolis */}
        <div className="flex items-center border border-slate-200 bg-white rounded-xl p-2.5 shadow-sm hover:border-teal-300 transition group flex-1">
          <div className="w-48 flex-shrink-0 border-r border-slate-100 pr-3">
            <div className="flex items-center space-x-2 mb-1">
              <Favicon domain="metropolis.io" className="w-4 h-4 rounded shadow-sm" />
              <span className="font-bold text-slate-900 text-xs">Metropolis <span className="font-medium text-[9px] text-slate-400 ml-0.5">(USA)</span></span>
            </div>
            <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[9px] font-bold font-mono-custom block w-fit">TAKE-PRIVATE ($1.5B)</span>
          </div>
          <div className="flex-1 px-4 border-r border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">The Strategic Move</span>
            <p className="text-[11px] text-slate-700 leading-snug">Pure-play computer vision startup acquired legacy operator SP+ (2M+ physical spaces). Stripped out legacy gate hardware and deployed proprietary checkout-free edge-AI vision across the footprint.</p>
          </div>
          <div className="flex-1 pl-4">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Board Takeaway</span>
            <p className="text-[11px] text-slate-700 leading-snug font-medium">Proves that acquiring physical space and instantly overlaying edge-AI software unlocks massive margin expansion by eliminating hardware capex.</p>
          </div>
        </div>

        {/* Row 2: EasyPark */}
        <div className="flex items-center border border-slate-200 bg-white rounded-xl p-2.5 shadow-sm hover:border-teal-300 transition group flex-1">
          <div className="w-48 flex-shrink-0 border-r border-slate-100 pr-3">
            <div className="flex items-center space-x-2 mb-1">
              <Favicon domain="easypark.com" className="w-4 h-4 rounded shadow-sm" />
              <span className="font-bold text-slate-900 text-xs">EasyPark Group <span className="font-medium text-[9px] text-slate-400 ml-0.5">(EU/Global)</span></span>
            </div>
            <span className="px-2 py-0.5 rounded bg-teal-50 text-teal-700 text-[9px] font-bold font-mono-custom block w-fit">GLOBAL ROLL-UP M&A</span>
          </div>
          <div className="flex-1 px-4 border-r border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">The Strategic Move</span>
            <p className="text-[11px] text-slate-700 leading-snug">PE-backed consolidator acquired BMW/Mercedes’ <strong>PARK NOW</strong> (mobile demand) and <strong>Flowbird</strong> (municipal supply/terminals), securing integration into major OEM in-dash systems.</p>
          </div>
          <div className="flex-1 pl-4">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Board Takeaway</span>
            <p className="text-[11px] text-slate-700 leading-snug font-medium">To survive at the Interface layer, you cannot rely on standalone phone apps. You must buy your way into connected-car dashboards and global clearing.</p>
          </div>
        </div>

        {/* Row 3: APCOA / Indigo */}
        <div className="flex items-center border border-slate-200 bg-white rounded-xl p-2.5 shadow-sm hover:border-teal-300 transition group flex-1">
          <div className="w-48 flex-shrink-0 border-r border-slate-100 pr-3">
            <div className="flex items-center space-x-2 mb-1">
              <Favicon domain="apcoa.com" className="w-4 h-4 rounded shadow-sm" />
              <span className="font-bold text-slate-900 text-xs">APCOA / Indigo <span className="font-medium text-[9px] text-slate-400 ml-0.5">(Europe)</span></span>
            </div>
            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-bold font-mono-custom block w-fit">UTILITY JV INFRA</span>
          </div>
          <div className="flex-1 px-4 border-r border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">The Strategic Move</span>
            <p className="text-[11px] text-slate-700 leading-snug">Converted aging multi-storey garages into "Urban Hubs" by forming JVs with <strong>Tesla/Shell</strong> for destination fast-charging and <strong>Amazon/DPD</strong> for last-mile micro-distribution.</p>
          </div>
          <div className="flex-1 pl-4">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Board Takeaway</span>
            <p className="text-[11px] text-slate-700 leading-snug font-medium">Garages are no longer car parks; they are decentralized micro-utilities. Off-balance-sheet SPVs are required to fund the heavy EV grid switchgear.</p>
          </div>
        </div>

        {/* Row 4: FLASH */}
        <div className="flex items-center border border-slate-200 bg-white rounded-xl p-2.5 shadow-sm hover:border-teal-300 transition group flex-1">
          <div className="w-48 flex-shrink-0 border-r border-slate-100 pr-3">
            <div className="flex items-center space-x-2 mb-1">
              <Favicon domain="flashparking.com" className="w-4 h-4 rounded shadow-sm" />
              <span className="font-bold text-slate-900 text-xs">FLASH OS <span className="font-medium text-[9px] text-slate-400 ml-0.5">(USA)</span></span>
            </div>
            <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[9px] font-bold font-mono-custom block w-fit">PLATFORM M&A</span>
          </div>
          <div className="flex-1 px-4 border-r border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">The Strategic Move</span>
            <p className="text-[11px] text-slate-700 leading-snug">Unicorn backed by Brookfield. Acquired consumer app <strong>Arrive</strong> and local software <strong>Ticketech</strong> to create a unified cloud OS connecting legacy garage hardware directly to enterprise fleets.</p>
          </div>
          <div className="flex-1 pl-4">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Board Takeaway</span>
            <p className="text-[11px] text-slate-700 leading-snug font-medium">Building software organically is too slow. Platform champions acquire established software and API clearing engines to instantly capture B2B enterprise fleets.</p>
          </div>
        </div>

        {/* Row 5: Salik / Parkonic */}
        <div className="flex items-center border border-slate-200 bg-white rounded-xl p-2.5 shadow-sm hover:border-teal-300 transition group flex-1">
          <div className="w-48 flex-shrink-0 border-r border-slate-100 pr-3">
            <div className="flex items-center space-x-2 mb-1">
              <Favicon domain="salik.ae" className="w-4 h-4 rounded shadow-sm" />
              <span className="font-bold text-slate-900 text-xs">Salik & Parkonic <span className="font-medium text-[9px] text-slate-400 ml-0.5">(UAE)</span></span>
            </div>
            <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[9px] font-bold font-mono-custom block w-fit">DOMESTIC CAPTURE</span>
          </div>
          <div className="flex-1 px-4 border-r border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">The Strategic Move</span>
            <p className="text-[11px] text-slate-700 leading-snug">Salik leveraged its 4M+ tolling accounts to capture Dubai Mall clearing. Parkonic aggressively locked up UAE master developers (Aldar, Line) via barrierless ANPR gantries.</p>
          </div>
          <div className="flex-1 pl-4">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Board Takeaway</span>
            <p className="text-[11px] text-slate-700 leading-snug font-medium text-rose-700">The domestic flank is under active attack. Parkin must consolidate private developer off-street real estate before regional insurgents lock them into long-term contracts.</p>
          </div>
        </div>

      </div>

      {/* Bottom Bridge Punchline mapped to the 3 New Theses */}
      <div className="mt-2.5 pt-2.5 border-t border-slate-200 flex-shrink-0">
        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest block mb-1.5">Direct Linkage to Parkin's Capital Allocation Theses</span>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-start space-x-2 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1 flex-shrink-0"></span>
            <span className="text-[10px] leading-snug text-slate-700"><strong>Metropolis & APCOA</strong> prove <strong className="text-teal-700">THESIS 01</strong> (Monetizing existing physical assets via AI & EV).</span>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-start space-x-2 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1 flex-shrink-0"></span>
            <span className="text-[10px] leading-snug text-slate-700"><strong>FLASH & Parkonic</strong> prove <strong className="text-indigo-700">THESIS 02</strong> (Expanding footprint asset-lightly via developer consolidation).</span>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex items-start space-x-2 shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1 flex-shrink-0"></span>
            <span className="text-[10px] leading-snug text-slate-700"><strong>EasyPark & Flowbird</strong> prove <strong className="text-emerald-700">THESIS 03</strong> (Decoupling and exporting software/interface independently).</span>
          </div>
        </div>
      </div>

    </div>
  </Slide>
);

const Slide7 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="CAPITAL DEPLOYMENT FRAMEWORK"
    slideTitle="Parkin Growth Theses: The 3-Vector Capital Allocation Strategy"
    subtitle="Deploying the AED 1.5B War Chest into strictly MECE high-ROIC growth engines"
    sourceText="Sources: Parkin Corporate Strategy 2026–2030; Internal Hurdle Rate Guidelines; WAM Disclosures (Amakin/Modon MoUs); DFM Developer Filings"
  >
    <div className="flex flex-col h-full py-2">
      <div className="grid grid-cols-3 gap-5 flex-1 min-h-0">
        
        {/* THESIS 1: Monetize Existing */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden hover:border-teal-300 transition group">
          <div className="bg-teal-50/50 p-4 border-b border-slate-100 flex-shrink-0">
            <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-800 text-[10px] font-bold font-mono-custom block w-fit mb-2">THESIS 01</span>
            <h3 className="font-bold text-slate-900 text-sm leading-snug">Monetize Existing Physical Assets Higher Up the Stack</h3>
            <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">Core Mandate: Extract multi-tiered software & energy yield from Parkin's captive 203k public Dubai bays and 9 legacy MSCPs.</p>
          </div>
          
          <div className="p-4 flex-1 overflow-y-auto space-y-4">
            <div>
              <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider font-mono-custom mb-1.5 block">01. Dynamic Curb Yield Engine</span>
              <p className="text-[11px] text-slate-600 leading-snug">Deploy edge-AI optical sensors across 2,500 high-density commercial curbs (Business Bay, Downtown). Transition from static AED 4/hr to dynamic per-minute billing, auto-billing quick-commerce fleets (Amazon, Talabat) for high-frequency 8-minute micro-dwells.</p>
            </div>
            <div>
              <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider font-mono-custom mb-1.5 block">02. Destination EV Energy Transition</span>
              <p className="text-[11px] text-slate-600 leading-snug">Monetize high-density off-street bays with AC/DC charging via utility partnerships. Capture blended revenue: parking dwell fees + kWh energy margins + automated post-charge idling penalties.</p>
            </div>
            <div>
              <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider font-mono-custom mb-1.5 block">03. MSCP Retrofit Repurposing</span>
              <p className="text-[11px] text-slate-600 leading-snug">Re-rate underutilized basement/roof decks of legacy garages (Al Rigga, Oud Metha) into turnkey Autonomous Vehicle (Cruise) turnaround staging depots and Skyports vertiport nodes.</p>
            </div>
          </div>

          <div className="p-3.5 bg-slate-50 border-t border-slate-100 flex-shrink-0 group-hover:bg-teal-50/30 transition">
            <div className="space-y-1.5 mb-2.5">
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">Capital Envelope:</span> <strong className="text-slate-800 font-mono-custom text-xs">AED 550M–600M</strong></div>
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">EBITDA Corridor:</span> <strong className="text-emerald-600 font-mono-custom text-xs">+AED 110M–140M</strong></div>
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">Target Return:</span> <strong className="text-teal-600 font-mono-custom text-xs">20%–25% ROIC</strong></div>
            </div>
            <div className="pt-2.5 border-t border-slate-200/60">
              <span className="text-[10px] font-bold text-slate-600 block mb-0.5">Financial Character:</span>
              <span className="text-[10px] text-slate-500 leading-snug block">High cash conversion (captive rights-of-way). Volume-indexed to quick-commerce delivery density. Payback: ~3.8 years.</span>
            </div>
          </div>
        </div>

        {/* THESIS 2: Expand Asset Coverage */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden hover:border-indigo-300 transition group">
          <div className="bg-indigo-50/50 p-4 border-b border-slate-100 flex-shrink-0">
            <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-bold font-mono-custom block w-fit mb-2">THESIS 02</span>
            <h3 className="font-bold text-slate-900 text-sm leading-snug">Expand Physical Asset Coverage Asset-Lightly</h3>
            <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">Core Mandate: Scale managed spaces across Abu Dhabi & private master developers without pouring concrete or taking balance-sheet debt.</p>
          </div>
          
          <div className="p-4 flex-1 overflow-y-auto space-y-4">
            <div>
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider font-mono-custom mb-1.5 block">01. Private Master Developer Consolidation</span>
              <p className="text-[11px] text-slate-600 leading-snug">Scale the recent successful DAMAC and Al-Futtaim playbooks (which surged private portfolio +68% YoY to 61k+ spaces). Preempt domestic challengers by securing exclusive 5-10 year contracts across Aldar, Majid Al Futtaim, and Nakheel assets.</p>
            </div>
            <div>
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider font-mono-custom mb-1.5 block">02. Multi-Emirate Geographic Expansion</span>
              <p className="text-[11px] text-slate-600 leading-snug">Break decisively beyond Dubai's borders. Establish dominant market share in Abu Dhabi (Al Reem, Yas Islands) and expand operational management into high-growth commercial hubs across the Northern Emirates.</p>
            </div>
            <div>
              <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider font-mono-custom mb-1.5 block">03. Turnkey Barrierless Modernization</span>
              <p className="text-[11px] text-slate-600 leading-snug">Replace landlords' failure-prone mechanical boom barriers with frictionless contactless ALPR tied to Parkin's clearing rail, capturing high-margin revenue-share while lowering developer opex by ~40%.</p>
            </div>
          </div>

          <div className="p-3.5 bg-slate-50 border-t border-slate-100 flex-shrink-0 group-hover:bg-indigo-50/30 transition">
            <div className="space-y-1.5 mb-2.5">
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">Capital Envelope:</span> <strong className="text-slate-800 font-mono-custom text-xs">AED 400M–450M</strong></div>
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">EBITDA Corridor:</span> <strong className="text-emerald-600 font-mono-custom text-xs">+AED 80M–105M</strong></div>
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">Target Return:</span> <strong className="text-teal-600 font-mono-custom text-xs">18%–22% ROIC</strong></div>
            </div>
            <div className="pt-2.5 border-t border-slate-200/60">
              <span className="text-[10px] font-bold text-slate-600 block mb-0.5">Financial Character:</span>
              <span className="text-[10px] text-slate-500 leading-snug block">Asset-light margin defense. Turnkey ALPR yields recurring management fees from Day 1. Payback: ~4.5 years.</span>
            </div>
          </div>
        </div>

        {/* THESIS 3: Decouple & Export */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden hover:border-emerald-300 transition group">
          <div className="bg-emerald-50/50 p-4 border-b border-slate-100 flex-shrink-0">
            <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold font-mono-custom block w-fit mb-2">THESIS 03</span>
            <h3 className="font-bold text-slate-900 text-sm leading-snug">Decouple and Export Software & Interface Independently</h3>
            <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">Core Mandate: Package proprietary algorithms, enforcement logic, and digital wallets into an asset-light PaaS model scaling across the GCC.</p>
          </div>
          
          <div className="p-4 flex-1 overflow-y-auto space-y-4">
            <div>
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider font-mono-custom mb-1.5 block">01. Sovereign Municipal PaaS Concessions</span>
              <p className="text-[11px] text-slate-600 leading-snug">Export Parkin's smart scan enforcement logic and digital dispute engine without foreign civil works. Target mega-scale Saudi Arabia (Riyadh) municipal privatizations via joint bidding consortiums (e.g., Tahakom) utilizing an 8-12% SaaS + BPO take-rate.</p>
            </div>
            <div>
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider font-mono-custom mb-1.5 block">02. Cross-Border GCC Mobility Wallet</span>
              <p className="text-[11px] text-slate-600 leading-snug">Operationalize the August 2026 Bahrain (Amakin) and Egypt (Modon) MoUs. Enable seamless cross-border app roaming (e.g., auto-debit for UAE drivers in Manama), establishing Parkin as the default transit clearinghouse.</p>
            </div>
            <div>
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider font-mono-custom mb-1.5 block">03. Horizontal Curbside Data Intelligence</span>
              <p className="text-[11px] text-slate-600 leading-snug">Embed data monetization across all pillars. Aggregate city-wide curb-dwell telemetry and predictive charging patterns into high-margin API feeds sold to urban planners and enterprise logistics networks.</p>
            </div>
          </div>

          <div className="p-3.5 bg-slate-50 border-t border-slate-100 flex-shrink-0 group-hover:bg-emerald-50/30 transition">
            <div className="space-y-1.5 mb-2.5">
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">Capital Envelope:</span> <strong className="text-slate-800 font-mono-custom text-xs">AED 400M–450M</strong></div>
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">EBITDA Corridor:</span> <strong className="text-emerald-600 font-mono-custom text-xs">+AED 60M–85M</strong></div>
              <div className="flex items-center justify-between"><span className="text-[11px] text-slate-500 font-medium">Target Return:</span> <strong className="text-teal-600 font-mono-custom text-xs">22%–26%+ ROIC</strong></div>
            </div>
            <div className="pt-2.5 border-t border-slate-200/60">
              <span className="text-[10px] font-bold text-slate-600 block mb-0.5">Financial Character:</span>
              <span className="text-[10px] text-slate-500 leading-snug block">Valuation multiple catalyst (70%+ SaaS margins). Zero cross-border balance-sheet debt. High terminal value.</span>
            </div>
          </div>
        </div>

      </div>

      <div className="mt-3 p-2.5 rounded-lg bg-slate-100 border border-slate-200 text-[9.5px] text-slate-500 shadow-sm leading-relaxed flex-shrink-0">
        <strong className="text-slate-700">Underwriting Methodology & Evaluation Parameters:</strong> Financial figures represent indicative underwriting corridors derived from bottom-up pilot benchmarks, peer SaaS multiples (70%+ gross margins), and historical developer contract yields. All initiatives are subject to individual project hurdle rates of {`>`}18%–20% ROIC and formal Board Investment Committee review prior to definitive capital commitment. Total Projected Portfolio EBITDA: +AED 250M – 330M.
      </div>
    </div>
  </Slide>
);

const Slide8 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="EXECUTION BLUEPRINT — THESIS 01"
    slideTitle="Monetizing Existing Assets Higher Up the Stack: Capability Gaps & Action Blueprint"
    subtitle="Detailed capability audit and delivery vehicles across Parkin's captive 203k Dubai bays and 9 central MSCPs"
    sourceText="Sources: RTA Loading Zone Pilot Data; DEWA EV Green Charger Framework; Parkin Fleet Telemetry; Global M&A Due Diligence Screenings"
  >
    <div className="flex flex-col h-full py-1">
      
      {/* TOP BANNER */}
      <div className="bg-slate-900 rounded-xl p-3 text-white flex items-center justify-between shadow-md flex-shrink-0 mb-3 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="flex items-center space-x-5 relative z-10">
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">Capital Envelope</span> <strong className="text-white font-mono-custom text-sm">AED 550M–600M</strong></div>
          <div className="w-px h-7 bg-slate-700"></div>
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">EBITDA Corridor</span> <strong className="text-emerald-400 font-mono-custom text-sm">+AED 110M–140M</strong></div>
          <div className="w-px h-7 bg-slate-700"></div>
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">Target ROIC</span> <strong className="text-teal-400 font-mono-custom text-sm">20% – 25%</strong></div>
        </div>
        <div className="text-[9px] text-slate-300 max-w-md leading-snug border-l border-slate-700 pl-4 relative z-10">
          <strong className="text-white block mb-0.5">Capital Discipline Rule:</strong> 
          Zero balance-sheet concrete or sensor manufacturing; capital is strictly concentrated in Software IP, APIs & SPVs.
        </div>
      </div>

      {/* 4-LAYER CAPABILITY MATRIX */}
      <div className="flex-1 flex flex-col justify-between space-y-2 min-h-0">
        
        {/* Layer 4: Interface */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-teal-300 transition">
          <div className="w-10 flex-shrink-0 bg-teal-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L4 INTERFACE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100">
            {/* Card 1 */}
            <div className="p-2.5 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">B2B Fleet Direct Clearing API Gateway</h3>
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUILD (IN-HOUSE)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1.5"><strong className="text-rose-700">Gap:</strong> Only consumer app exists; couriers pay individually or evade, creating friction and unbilled micro-dwells.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-teal-700">Why Build?</strong> Parkin owns the curb. Direct API billing unlocks 90%+ margin ARR without SaaS middlemen.</div>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between mt-2">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1.5">Target Fleet Clients:</span>
                  <div className="flex flex-wrap gap-x-2.5 gap-y-1.5 items-center text-[10px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1.5"><Favicon domain="amazon.ae" className="w-3 h-3 rounded-sm" /> <span>Amazon</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="talabat.com" className="w-3 h-3 rounded-sm" /> <span>Talabat</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="noon.com" className="w-3 h-3 rounded-sm" /> <span>Noon</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="aramex.com" className="w-3 h-3 rounded-sm" /> <span>Aramex</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="dhl.com" className="w-3 h-3 rounded-sm" /> <span>DHL</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="deliveroo.ae" className="w-3 h-3 rounded-sm" /> <span>Deliveroo</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 30M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 20M–25M</div>
                </div>
              </div>
            </div>
            {/* Empty col for symmetry */}
            <div className="p-2.5 flex flex-col justify-between bg-slate-50/30"></div>
          </div>
        </div>

        {/* Layer 3: Software */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-indigo-300 transition">
          <div className="w-10 flex-shrink-0 bg-indigo-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L3 SOFTWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100 bg-white">
            {/* Card 2A */}
            <div className="p-2.5 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Dynamic Curbside Yield Pricing Engine</h3>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUY / LICENSE</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1.5"><strong className="text-rose-700">Gap:</strong> Static AED 4/hr tariffs; lacks real-time optical dwell-matching ML and per-minute commercial pricing.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-amber-700">Why Buy?</strong> ML models take 2+ years to train. Acquiring IP captures 2,500 curbside yields in 90 days.</div>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between mt-2">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1.5">M&A / IP Targets:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1.5 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="automotus.co" className="w-3 h-3 rounded-sm" /> <span>Automotus</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="populus.ai" className="w-3 h-3 rounded-sm" /> <span>Populus</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="curbiq.com" className="w-3 h-3 rounded-sm" /> <span>CurbIQ</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 120M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 45M–55M</div>
                </div>
              </div>
            </div>
            {/* Card 2B */}
            <div className="p-2.5 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Enterprise Cloud CPMS Platform</h3>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUY / WHITE-LABEL</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1.5"><strong className="text-rose-700">Gap:</strong> Zero EV software IP; unable to balance grid load, automate idle fees, or clear multi-operator charging.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-amber-700">Why Buy?</strong> White-labeling a proven enterprise OS instantly secures 70%+ SaaS margins on energy throughput.</div>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between mt-2">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1.5">M&A / IP Targets:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1.5 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="ampeco.com" className="w-3 h-3 rounded-sm" /> <span>AMPECO</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="driivz.com" className="w-3 h-3 rounded-sm" /> <span>Driivz</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="virta.global" className="w-3 h-3 rounded-sm" /> <span>Virta</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 150M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 30M–35M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layer 2: Hardware */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-amber-300 transition">
          <div className="w-10 flex-shrink-0 bg-amber-500 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L2 HARDWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100 bg-white">
            {/* Card 3A */}
            <div className="p-2.5 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Fixed Optical Edge-AI Street Cameras</h3>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">OUTSOURCE</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1.5"><strong className="text-rose-700">Gap:</strong> Mobile scan cars miss 80%+ of short courier stops due to 45-min patrol cycles; fixed cameras required.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-slate-600">Why Outsource?</strong> Hardware is a commodity trap (sub-30% margin). Procure off-the-shelf as pass-through opex.</div>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between mt-2">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1.5">Procurement OEM Vendors:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1.5 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="tagmaster.com" className="w-3 h-3 rounded-sm" /> <span>TagMaster</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="axis.com" className="w-3 h-3 rounded-sm" /> <span>Axis</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: Pass-through</div>
                </div>
              </div>
            </div>
            {/* Card 3B */}
            <div className="p-2.5 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">EV Fast Dispensers & Switchgear</h3>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">PARTNER (50:50 SPV)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1.5"><strong className="text-rose-700">Gap:</strong> 1,500 fast chargers require massive balance-sheet CapEx for DC switchgear, cabling, and transformers.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-purple-700">Why Partner?</strong> Shields statutory balance sheet. Utility partner finances & depreciates heavy electrical assets.</div>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between mt-2">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1.5">Consortium Partners:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1.5 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="dewa.gov.ae" className="w-3 h-3 rounded-sm" /> <span>DEWA</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="eand.com" className="w-3 h-3 rounded-sm" /> <span>e& (E2GO)</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="adnocdistribution.ae" className="w-3 h-3 rounded-sm" /> <span>ADNOC Dist.</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="abb.com" className="w-3 h-3 rounded-sm" /> <span>ABB</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 180M Eq.</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 15M–20M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layer 1: Physical */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-slate-400 transition">
          <div className="w-10 flex-shrink-0 bg-slate-700 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L1 PHYSICAL</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100">
            {/* Card 4 */}
            <div className="p-2.5 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">MSCP Repurposing for AV Fleet Staging</h3>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">PARTNER (B2B LEASE)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1.5"><strong className="text-rose-700">Gap:</strong> 9 MSCP garages have 30%+ excess off-peak capacity, yielding low ROI on invested concrete.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-purple-700">Why Partner?</strong> Parkin captures guaranteed, take-or-pay multi-year commercial rental yields with tenant-funded fit-outs.</div>
              </div>
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between mt-2">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1.5">Consortium Partners:</span>
                  <div className="flex flex-wrap gap-x-3 gap-y-1.5 items-center text-[10px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1.5"><Favicon domain="getcruise.com" className="w-3.5 h-3.5 rounded shadow-sm" /> <span>GM Cruise</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="weride.ai" className="w-3.5 h-3.5 rounded shadow-sm" /> <span>WeRide</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="skyports.net" className="w-3.5 h-3.5 rounded shadow-sm" /> <span>Skyports</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="jobyaviation.com" className="w-3.5 h-3.5 rounded shadow-sm" /> <span>Joby Aviation</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 70M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 15M–20M</div>
                </div>
              </div>
            </div>
            {/* Empty col for symmetry */}
            <div className="p-2.5 flex flex-col justify-between bg-slate-50/30"></div>
          </div>
        </div>

      </div>
    </div>
  </Slide>
);

const Slide9 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="EXECUTION BLUEPRINT — THESIS 02"
    slideTitle="Expanding Asset Coverage Asset-Lightly: Developer Consolidation"
    subtitle="Acquiring private developer off-street supply across the UAE without balance-sheet debt or civil works"
    sourceText="Sources: Parkin Developer Agreements (DAMAC 5-Yr, MAF MoUs); AD Mobility Regulatory Framework; DFM Real Estate Filings (Aldar, Emaar)"
  >
    <div className="flex flex-col h-full py-1">
      
      {/* TOP BANNER */}
      <div className="bg-slate-900 rounded-xl p-2.5 text-white flex items-center justify-between shadow-md flex-shrink-0 mb-2 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="flex items-center space-x-5 relative z-10">
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">Capital Envelope</span> <strong className="text-white font-mono-custom text-sm">AED 400M–450M</strong></div>
          <div className="w-px h-7 bg-slate-700"></div>
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">EBITDA Corridor</span> <strong className="text-emerald-400 font-mono-custom text-sm">+AED 80M–105M</strong></div>
          <div className="w-px h-7 bg-slate-700"></div>
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">Target ROIC</span> <strong className="text-indigo-400 font-mono-custom text-sm">18% – 22%</strong></div>
        </div>
        <div className="text-[9px] text-slate-300 max-w-md leading-snug border-l border-slate-700 pl-4 relative z-10">
          <strong className="text-white block mb-0.5">Capital Discipline Rule:</strong> 
          Zero balance-sheet concrete or garage acquisitions. Capital is strictly deployed toward ALPR turnkey gantries, M&A IP carve-outs, and developer revenue-share onboarding.
        </div>
      </div>

      {/* 4-LAYER CAPABILITY MATRIX */}
      <div className="flex-1 flex flex-col justify-between space-y-1.5 min-h-0">
        
        {/* Layer 4: Interface */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-teal-300 transition">
          <div className="w-10 flex-shrink-0 bg-teal-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L4 INTERFACE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100">
            {/* Card 1 */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Unified Multi-Emirate Digital Clearing Hub</h3>
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUILD (IN-HOUSE)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Motorists crossing emirates face fragmented apps and siloed municipal tolling accounts (Salik vs Darb/Mawaqif).</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-teal-700">Why Build?</strong> Building a unified clearing layer creates captive user lock-in, capturing frictionless micro-transaction processing fees across all developer sites.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Integration Rails:</span>
                  <div className="flex flex-wrap gap-x-2.5 gap-y-1 items-center text-[10px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1.5"><Favicon domain="admobility.gov.ae" /> <span>Mawaqif</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="darb.ae" /> <span>Darb</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="salik.ae" /> <span>Salik</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="apple.com" /> <span>Apple Pay</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 25M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 15M–20M</div>
                </div>
              </div>
            </div>
            {/* Empty col for symmetry */}
            <div className="p-2 flex flex-col justify-between bg-slate-50/30"></div>
          </div>
        </div>

        {/* Layer 3: Software */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-indigo-300 transition">
          <div className="w-10 flex-shrink-0 bg-indigo-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L3 SOFTWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100 bg-white">
            {/* Card 2A */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Multi-Tenant Landlord PMS & Yield Portal</h3>
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUILD (IN-HOUSE)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Private mall/residential landlords lack real-time visibility into yields, tenant validations, and automated dynamic event pricing.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-teal-700">Why Build?</strong> Developer stickiness. Enterprise-grade operational transparency justifies an 8%–12% SaaS management fee and cements 5-10 year lock-ins.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Target Landlords:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="aldar.com" /> <span>Aldar</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="majidalfuttaim.com" /> <span>Majid Al Futtaim</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="emaar.com" /> <span>Emaar</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="nakheel.com" /> <span>Nakheel</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 35M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 20M–25M</div>
                </div>
              </div>
            </div>
            {/* Card 2B */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Parkonic Software & ANPR Carve-Out</h3>
                  <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUY / CARVE-OUT</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Domestic insurgent Parkonic has aggressively captured private retail lots (170+ sites) using high-speed barrierless ALPR gates.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-amber-700">Why Buy?</strong> Instantly neutralizes the primary domestic competitor and acquires their software IP/contracts while avoiding their hardware liabilities.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">M&A / IP Targets:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="parkonic.com" /> <span>Parkonic OS</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="skidata.com" /> <span>SKIDATA Connect</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="scheidt-bachmann.com" /> <span>Scheidt & Bachmann</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 140M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 25M–30M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layer 2: Hardware */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-amber-300 transition">
          <div className="w-10 flex-shrink-0 bg-amber-500 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L2 HARDWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100 bg-white">
            {/* Card 3A */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Turnkey Frictionless ALPR Gantries</h3>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">OUTSOURCE</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Developers want to replace broken physical boom barriers with contactless ALPR gantries, but Parkin should not manufacture hardware.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-slate-600">Why Outsource?</strong> Manufacturing physical gates delivers low margins. Procuring standardized cameras as a pass-through cost keeps the balance sheet asset-light.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Procurement OEM Vendors:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="tattile.com" /> <span>Tattile</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="nedap.com" /> <span>Nedap</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="hikvision.com" /> <span>Hikvision</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: Pass-through</div>
                </div>
              </div>
            </div>
            {/* Card 3B */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Private Community EV Charging Hubs</h3>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">PARTNER (REV-SHARE)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Private developer residential master communities (e.g., DAMAC Hills, Yas Acres) demand EV destination chargers without landlord capex.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-purple-700">Why Partner?</strong> Utility partners fund and operate the chargers. Parkin captures software management fees and transaction margins with zero electrical capex.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Consortium Partners:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="adnocdistribution.ae" /> <span>E2GO / ADNOC</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="taqa.com" /> <span>TAQA</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="dewa.gov.ae" /> <span>DEWA</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="wallbox.com" /> <span>Wallbox</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 40M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 10M–15M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layer 1: Physical */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-slate-400 transition">
          <div className="w-10 flex-shrink-0 bg-slate-700 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L1 PHYSICAL</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100">
            {/* Card 4 */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Master Developer Exclusive Concessions (MSAs)</h3>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">PARTNER (5–10 YR)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Commercial and residential parking in high-growth districts (Downtown, Yas Island, Dubai South) is locked behind private developer master agreements.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-purple-700">Why Partner?</strong> Requires zero balance-sheet land purchases. Parkin provides turnkey barrierless operations and captures a recurring 20%–35% share of gross revenue.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Target Landlords:</span>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 items-center text-[10px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1.5"><Favicon domain="damacproperties.com" /> <span>DAMAC</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="majidalfuttaim.com" /> <span>Majid Al Futtaim</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="aldar.com" /> <span>Aldar</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="nakheel.com" /> <span>Nakheel</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="sobha.com" /> <span>Sobha Realty</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 110M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 20M–25M</div>
                </div>
              </div>
            </div>
            {/* Empty col for symmetry */}
            <div className="p-2 flex flex-col justify-between bg-slate-50/30"></div>
          </div>
        </div>

      </div>
    </div>
  </Slide>
);

const Slide10 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="EXECUTION BLUEPRINT — THESIS 03"
    slideTitle="Decoupling & Exporting Software Independently: Sovereign PaaS"
    subtitle="Packaging Parkin's enforcement logic, digital wallets, and curbside analytics into asset-light GCC concessions"
    sourceText="Sources: WAM Disclosures (Amakin Bahrain / Modon Egypt MoUs); Remat Al-Riyadh Concession Tenders; Tahakom Regulatory Framework"
  >
    <div className="flex flex-col h-full py-1">
      
      {/* TOP BANNER */}
      <div className="bg-slate-900 rounded-xl p-2.5 text-white flex items-center justify-between shadow-md flex-shrink-0 mb-2 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="flex items-center space-x-5 relative z-10">
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">Capital Envelope</span> <strong className="text-white font-mono-custom text-sm">AED 400M–450M</strong></div>
          <div className="w-px h-7 bg-slate-700"></div>
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">EBITDA Corridor</span> <strong className="text-emerald-400 font-mono-custom text-sm">+AED 60M–85M</strong></div>
          <div className="w-px h-7 bg-slate-700"></div>
          <div><span className="text-[9px] text-slate-400 block uppercase tracking-wider mb-0.5 font-mono-custom">Target ROIC</span> <strong className="text-emerald-400 font-mono-custom text-sm">22% – 26%+</strong></div>
        </div>
        <div className="text-[9px] text-slate-300 max-w-md leading-snug border-l border-slate-700 pl-4 relative z-10">
          <strong className="text-white block mb-0.5">Capital Discipline Rule:</strong> 
          Zero cross-border civil capex or concrete debt. Capital is strictly deployed into multi-tenant cloud PaaS, GCC wallet interoperability, and minority consortium equity.
        </div>
      </div>

      {/* 4-LAYER CAPABILITY MATRIX */}
      <div className="flex-1 flex flex-col justify-between space-y-1.5 min-h-0">
        
        {/* Layer 4: Interface */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-teal-300 transition">
          <div className="w-10 flex-shrink-0 bg-teal-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L4 INTERFACE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100">
            {/* Card 1 */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">GCC Cross-Border Mobility Roaming Wallet</h3>
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUILD (IN-HOUSE)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Gulf motorists driving between Dubai, KSA, and Bahrain encounter fragmented apps, currency conversions, and unpaid foreign violations.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-teal-700">Why Build?</strong> Eliminates friction. Parkin becomes the de facto regional clearinghouse, earning a high-margin interchange fee on cross-border dwells.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Integration Rails:</span>
                  <div className="flex flex-wrap gap-x-2.5 gap-y-1 items-center text-[10px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1.5"><Favicon domain="bahraincarparks.com" /> <span>Amakin (BH)</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="modon.gov.eg" /> <span>Modon (EG)</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="darb.ae" /> <span>Darb</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="buna.co" /> <span>Buna Rail</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 35M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 15M–20M</div>
                </div>
              </div>
            </div>
            {/* Empty col for symmetry */}
            <div className="p-2 flex flex-col justify-between bg-slate-50/30"></div>
          </div>
        </div>

        {/* Layer 3: Software */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-indigo-300 transition">
          <div className="w-10 flex-shrink-0 bg-indigo-600 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L3 SOFTWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100 bg-white">
            {/* Card 2A */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Sovereign PaaS Enforcement & Dispute Engine</h3>
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUILD / EXPORT</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Regional municipalities (e.g., Riyadh) are privatizing curbs but lack mature ANPR citation workflows and digital fine dispute backends.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-teal-700">Why Export?</strong> Pure SaaS economics. Deploying a white-labeled OS captures an 8%–12% take-rate on violation processing with zero real estate risk.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Target Municipal Clients:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="remat.sa" /> <span>Remat Al-Riyadh</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="tahakom.com" /> <span>Tahakom (KSA)</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="momrah.gov.sa" /> <span>MOMRAH</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 145M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 25M–35M</div>
                </div>
              </div>
            </div>
            {/* Card 2B */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Horizontal Curbside Data Intelligence (DaaS)</h3>
                  <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">BUILD (DaaS)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Urban delivery fleets, AV developers, and city planners lack real-time telemetry on curb occupancy and double-parking friction.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-teal-700">Why Monetize?</strong> 95%+ incremental gross margin. Repackaging scan telemetry into predictive APIs incurs zero marginal cost and creates sticky recurring ARR.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Target Data Consumers:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="here.com" /> <span>HERE Tech</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="tomtom.com" /> <span>TomTom</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="google.com" /> <span>Google Mobility</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="arcadis.com" /> <span>CurbIQ</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 40M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 10M–15M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layer 2: Hardware */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:border-amber-300 transition">
          <div className="w-10 flex-shrink-0 bg-amber-500 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L2 HARDWARE</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100 bg-white">
            {/* Card 3A */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Turnkey Smart Scan Vehicle & Edge Racks</h3>
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">OUTSOURCE</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Foreign municipal concessions require fleets of mobile ANPR vehicles, but Parkin must avoid holding depreciating vehicle assets abroad.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-slate-600">Why Outsource?</strong> Package the mobile kit as a COTS specification. Local municipalities fund the hardware directly; Parkin charges integration fees.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Procurement OEM Vendors:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="genetec.com" /> <span>Genetec AutoVu</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="tattile.com" /> <span>Tattile Edge</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="jenoptik.com" /> <span>Jenoptik</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 30M</div>
                </div>
              </div>
            </div>
            {/* Card 3B */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Sovereign Sensor & Cloud Infrastructure SPV</h3>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">PARTNER (CONSORTIUM)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Sovereign clients (e.g., KSA) require strict in-country data residency compliance and localized physical server infrastructure.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-purple-700">Why Partner?</strong> Regional tech giants fund the local server capacity. Parkin brings the algorithmic software layer and takes equity in the JV.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Consortium Partners:</span>
                  <div className="flex flex-wrap gap-x-2 gap-y-1 items-center text-[9.5px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1"><Favicon domain="alat.com" /> <span>Alat / PIF</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="tahakom.com" /> <span>Tahakom</span></span>
                    <span className="flex items-center space-x-1"><Favicon domain="solutions.com.sa" /> <span>solutions by stc</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 60M Eq.</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 5M–10M</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Layer 1: Physical */}
        <div className="flex-1 flex items-stretch border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm hover:border-slate-400 transition">
          <div className="w-10 flex-shrink-0 bg-slate-700 flex flex-col items-center justify-center">
            <span className="text-white font-bold text-[9px] uppercase -rotate-90 whitespace-nowrap tracking-widest font-mono-custom mt-auto mb-auto">L1 PHYSICAL</span>
          </div>
          <div className="flex-1 grid grid-cols-2 divide-x divide-slate-100">
            {/* Card 4 */}
            <div className="p-2 flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-slate-900 text-xs">Asset-Light Municipal Bidding Consortiums</h3>
                  <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[8.5px] font-bold font-mono-custom tracking-wider flex-shrink-0 ml-2">PARTNER (ZERO-DEBT)</span>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug mb-1"><strong className="text-rose-700">Gap:</strong> Major regional tenders require massive upfront performance bonds and civil works guarantees that would strain Parkin's balance sheet.</p>
                <div className="text-[9.5px] text-slate-500 leading-snug"><strong className="text-purple-700">Why Partner?</strong> Act exclusively as the Technology Operator. Local infrastructure funds provide the balance-sheet guarantees; Parkin captures recurring management fees.</div>
              </div>
              <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-slate-500">
                  <span className="font-semibold text-slate-700 block mb-1">Target Municipal Concessions:</span>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 items-center text-[10px] text-slate-600 font-medium">
                    <span className="flex items-center space-x-1.5"><Favicon domain="remat.sa" /> <span>Riyadh Tenders</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="jeddah.gov.sa" /> <span>Jeddah Municipality</span></span>
                    <span className="flex items-center space-x-1.5"><Favicon domain="eastern.gov.sa" /> <span>Eastern Province</span></span>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end justify-between">
                  <div className="text-[9px] font-mono-custom font-semibold text-slate-400 bg-slate-50 px-2 py-1 rounded border border-slate-100 mb-1 whitespace-nowrap">Capital: AED 90M</div>
                  <div className="text-[10px] font-mono-custom font-bold text-teal-600 whitespace-nowrap">EBITDA: +AED 15M–20M</div>
                </div>
              </div>
            </div>
            {/* Empty col for symmetry */}
            <div className="p-2 flex flex-col justify-between bg-slate-50/30"></div>
          </div>
        </div>

      </div>
    </div>
  </Slide>
);

const Slide11 = ({ pageNumber, totalPages }) => (
  <Slide 
    pageNumber={pageNumber}
    totalPages={totalPages}
    sectionTitle="DEAL UNDERWRITING & DILIGENCE ARCHITECTURE"
    slideTitle="Strategic Deal Rationale: Exemplar Target Underwriting"
    subtitle="Deep-dive commercial logic, valuation arbitrage, and risk moats across two representative transaction archetypes"
    sourceText="Sources: PitchBook SaaS Multiples; Enterprise M&A Precedents; Parkin Corporate Strategy Underwriting Guidelines"
  >
    <div className="flex flex-col h-full py-1">
      
      {/* Top Underwriting & Governance Banner */}
      <div className="bg-slate-900 rounded-xl p-3 text-white shadow-md flex-shrink-0 mb-3 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="relative z-10 flex items-start space-x-3">
          <div className="mt-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-teal-400"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
          </div>
          <div>
            <h4 className="text-xs font-bold text-teal-400 tracking-wider uppercase mb-0.5 font-mono-custom">Investment Diligence Standard</h4>
            <p className="text-[11px] text-slate-300 leading-snug">
              The Investment Committee evaluates every inorganic move against four strict gates: (1) Immediate multiple re-rating, (2) Zero concrete/hardware Capex, (3) Structural protection of statutory dividend conversion, and (4) Contractual defensibility. The two targets below demonstrate this methodology across legacy installed systems and cutting-edge vision IP.
            </p>
          </div>
        </div>
      </div>

      {/* 2-Column Exemplar Deep Dives */}
      <div className="grid grid-cols-2 gap-6 flex-1 min-h-0">
        
        {/* COLUMN 1: SCHEIDT & BACHMANN */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden hover:border-indigo-300 transition group">
          <div className="bg-indigo-50/50 p-4 border-b border-slate-100 flex items-start justify-between flex-shrink-0">
            <div>
              <div className="flex items-center space-x-2 mb-1.5">
                <Favicon domain="scheidt-bachmann.com" className="w-4 h-4 rounded shadow-sm bg-white" />
                <h3 className="font-bold text-slate-900 text-sm">Scheidt & Bachmann</h3>
              </div>
              <p className="text-[11px] text-slate-600 font-medium">Installed Base & Software Bridge</p>
              <div className="mt-2 flex items-center space-x-1.5 text-[8.5px] text-slate-500 font-mono-custom">
                <span>HQ: Mönchengladbach, DE</span> <span className="text-indigo-200">|</span> <span>Est. 1872</span> <span className="text-indigo-200">|</span> <span>Private Family</span> <span className="text-indigo-200">|</span> <span>~3,000 FTEs</span>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[9px] font-bold font-mono-custom tracking-wider">L3 REGIONAL IP CARVE-OUT</span>
              <div className="text-[10px] font-mono-custom text-slate-500 font-semibold text-right whitespace-nowrap">
                <span className="text-slate-400 font-medium">Indicative Envelope:</span> AED 140M–160M <br/>
                <span className="text-slate-400 font-medium">Target EBITDA:</span> <span className="text-emerald-600">+AED 20M–25M</span><br/>
                <span className="text-slate-400 font-medium">Underwriting Hurdle:</span> <span className="text-indigo-600">18%–21% ROIC</span>
              </div>
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col justify-between overflow-y-auto text-[11px]">
            <div>
              <span className="font-bold text-indigo-700 block mb-1">Strategic Thesis & Deal Structure</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">The Market Trap:</strong> S&amp;B controls premier Gulf hubs (airports, Bluewaters, hospitals, major commercial centers). Master developers refuse to rip out amortized gate hardware costing tens of millions.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Deal Structure:</strong> Primary: Asset Carve-Out (Regional IP & API Middleware) | Fallback: 10-Yr Exclusive Regional Master License.</span></li>
              </ul>
            </div>
            
            <div className="pt-3 border-t border-slate-100">
              <span className="font-bold text-slate-800 block mb-1">Commercial & Operational Synergies</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Zero Landlord Capex:</strong> Gives developers seamless digital ticketless access without replacing physical barriers.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Captive Onboarding:</strong> Instantly brings 35,000+ off-street bays into the Parkin App network on Day 1.</span></li>
              </ul>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <span className="font-bold text-slate-800 block mb-1">Financial & Multiple Arbitrage</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Acquisition Multiple:</strong> Legacy access software &amp; hardware trades at modest <strong className="text-slate-800">7x–9x EV/EBITDA</strong> multiples.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Integration Play:</strong> Routing S&amp;B transactions through a digital wallet re-rates these cash flows at a higher <strong className="text-emerald-600">16x+</strong> platform multiple.</span></li>
              </ul>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <span className="font-bold text-slate-800 block mb-1">Risk Moat & Downside Protection</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Liability Shield:</strong> Strict carve-out contract excludes on-the-ground mechanical gate repairs and low-margin field labor.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-indigo-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Defensive Flank:</strong> Blocks domestic challengers from securing exclusivity over prime commercial lots.</span></li>
              </ul>
            </div>
          </div>
        </div>

        {/* COLUMN 2: AUTOMOTUS / POPULUS */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm flex flex-col overflow-hidden hover:border-teal-300 transition group">
          <div className="bg-teal-50/50 p-4 border-b border-slate-100 flex items-start justify-between flex-shrink-0">
            <div>
              <div className="flex items-center space-x-2 mb-1.5">
                <Favicon domain="automotus.co" className="w-4 h-4 rounded shadow-sm bg-white" />
                <h3 className="font-bold text-slate-900 text-sm">Automotus (Comp: Populus)</h3>
              </div>
              <p className="text-[11px] text-slate-600 font-medium">Proprietary Edge-AI Curbside IP</p>
              <div className="mt-2 flex items-center space-x-1.5 text-[8.5px] text-slate-500 font-mono-custom">
                <span>HQ: Los Angeles, USA</span> <span className="text-teal-200">|</span> <span>Est. 2017</span> <span className="text-teal-200">|</span> <span>VC-Backed (Series A)</span> <span className="text-teal-200">|</span> <span>30+ Cities</span>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-800 text-[9px] font-bold font-mono-custom tracking-wider">L3 TECH IP / SOURCE CODE BUY</span>
              <div className="text-[10px] font-mono-custom text-slate-500 font-semibold text-right whitespace-nowrap">
                <span className="text-slate-400 font-medium">Indicative Envelope:</span> AED 120M–130M <br/>
                <span className="text-slate-400 font-medium">Target EBITDA:</span> <span className="text-emerald-600">+AED 45M–55M</span><br/>
                <span className="text-slate-400 font-medium">Underwriting Hurdle:</span> <span className="text-teal-600">23%–27% ROIC</span>
              </div>
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col justify-between overflow-y-auto text-[11px]">
            <div>
              <span className="font-bold text-teal-700 block mb-1">Strategic Thesis & Deal Structure</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">The Market Gap:</strong> 80%+ of urban delivery fleets dwell for 6–10 minutes, escaping static hourly billing. Internal AI models require 2+ years of edge-case training.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Deal Structure:</strong> Primary: Outright IP & Source Code Acquisition | Fallback: Exclusive GCC White-Label SaaS License with ROFR.</span></li>
              </ul>
            </div>
            
            <div className="pt-3 border-t border-slate-100">
              <span className="font-bold text-slate-800 block mb-1">Commercial & Operational Synergies</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">High-Margin Fleet Monetization:</strong> Automatically detects vehicle type and dwell minutes; triggers API billing to delivery fleets on a per-minute tariff.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">City Flow Efficiency:</strong> Eliminates double parking in high-friction districts.</span></li>
              </ul>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <span className="font-bold text-slate-800 block mb-1">Financial & Multiple Arbitrage</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">SaaS Economics:</strong> Pure software revenue model yields an <strong className="text-slate-800">85%+ gross margin</strong> on captured variable micro-dwells.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Valuation Catalyst:</strong> Turns unbilled curb friction into high-margin EBITDA, commanding pure SaaS multiples (<strong className="text-emerald-600">18x–25x</strong>).</span></li>
              </ul>
            </div>

            <div className="pt-3 border-t border-slate-100">
              <span className="font-bold text-slate-800 block mb-1">Risk Moat & Downside Protection</span>
              <ul className="space-y-1.5 text-slate-600 ml-1">
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Hardware Agnostic:</strong> Software runs on commodity, off-the-shelf optical cameras, protecting Parkin from sensor manufacturing obsolescence.</span></li>
                <li className="flex items-start space-x-1.5"><span className="text-teal-400 mt-0.5">•</span> <span><strong className="font-semibold text-slate-700">Data Network Effects:</strong> Every million dwell events captured improves model accuracy, creating an insurmountable barrier to entry.</span></li>
              </ul>
            </div>
          </div>
        </div>

      </div>

      {/* Bottom Underwriting Methodology & Evaluation Filter */}
      <div className="mt-3 p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[10px] text-slate-500 shadow-sm leading-relaxed flex-shrink-0 flex items-start space-x-3">
        <div className="mt-0.5"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="3" x2="9" y2="21"></line></svg></div>
        <div>
          <strong className="text-slate-700 block mb-0.5">Portfolio Underwriting Rigor</strong> 
          These transactions illustrate Parkin's dual-track AED 1.5B capital filter: acquiring cash-flowing installed bases at low multiples while securing high-margin edge software for untapped curb yield. Identical matrices govern all targets (AMPECO, Tahakom, DEWA, etc.).
        </div>
      </div>

    </div>
  </Slide>
);

export default function ParkinsPortfolioStrategy() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState('next');
  
  // Total number of slides
  const slides = [Slide1, Slide2, Slide3, Slide4, Slide5, Slide6, Slide7, Slide8, Slide9, Slide10, Slide11];
  const totalSlides = slides.length;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setDirection('next');
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setDirection('prev');
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleJumpTo = (index) => {
    if (index === currentSlide) return;
    setDirection(index > currentSlide ? 'next' : 'prev');
    setCurrentSlide(index);
  };

  return (
    <div className="p-4 md:p-8 selection:bg-teal-500 selection:text-white bg-[#F8FAFC] min-h-screen">
      <GlobalStyles />
      <Header 
        currentSlide={currentSlide} 
        totalSlides={totalSlides} 
        onPrev={handlePrev} 
        onNext={handleNext} 
        onJumpTo={handleJumpTo}
      />
      
      <div className="presentation-viewport">
        {slides.map((SlideComponent, index) => {
          // Determine the CSS class based on the slide's position relative to currentSlide
          let slideClass = '';
          if (index === currentSlide) {
            slideClass = 'active';
          } else if (index < currentSlide) {
            slideClass = 'prev'; // Slide has moved to the left (past)
          } else {
            slideClass = 'next'; // Slide is waiting on the right (future)
          }

          return (
            <div key={index} className={`slide-wrapper ${slideClass}`}>
              <SlideComponent pageNumber={index + 1} totalPages={totalSlides} />
            </div>
          );
        })}
      </div>
    </div>
  );
}